import { useCallback, useEffect } from 'react';
import AgoraRTC, { IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';
import {RtcTokenBuilder, RtcRole} from 'agora-access-token';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@redux/store';
import { setAnswered, setRtcClient } from '@redux/slices/voice-call/voice-call.slice';
import { agora } from '../../environments/voice-call-variables';


export interface BasicCall {
    name: string;
    id: string;
    token: string;
}

export interface CallInterface {
    name: string;
    nameEmit: string;
    id: string;
    token: string;
    room: string;
}

export interface CallRecive {
    nameEmit: string;
    name: string;
    id: string;
    idEmit: string;
    token: string;
    room: string;
}

export interface User {
    username: string
    id: string
    name: string
    role: string
}

/**
 * Use agora for voice call
 * @returns 
 */
export const useVoiceCall = () => {
    const rtc = useSelector((state: RootState) => state.voiceCall.rtc);
    AgoraRTC.setLogLevel(4);
    const dispatch = useDispatch();
    const appID = agora.appID;
    const appCertificate = agora.appCertificate;
    const role = RtcRole.PUBLISHER;
    const expirationTimeInSeconds = 3600
    
    const createCall = useCallback(async (channel: string) => {
        const privilegeExpiredTs = (Math.floor(Date.now() / 1000)) + expirationTimeInSeconds
        const token = RtcTokenBuilder.buildTokenWithUid(appID, appCertificate, channel, 0, role, privilegeExpiredTs);
        await rtc.client?.join(appID, channel, token);
        const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        await rtc.client?.publish([localAudioTrack])

        dispatch(setRtcClient({...rtc, localAudioTrack}));

        return {token, localAudioTrack}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, appID, role, rtc]);

    const joinCall = useCallback(async (channel: string, token: string) => {
        await rtc.client?.join( appID, channel, token );
        const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        // localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        
        await rtc.client?.publish([localAudioTrack]);
        
        dispatch(setRtcClient({...rtc, localAudioTrack}));
        return localAudioTrack
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, rtc]);
    
    const leaveCall = useCallback(async (localAudioTrack?: IMicrophoneAudioTrack) => {
        if (localAudioTrack) {
            localAudioTrack.close();
            await rtc.client?.leave();
        } else if(rtc.localAudioTrack) {
            rtc.localAudioTrack.close();
            await rtc.client?.leave();
        }
    }, [rtc]);
    
    useEffect(() => {
        const startBasicCall = async () => {
            const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

            client.on('user-published', async (user, mediaType) => {
                await client.subscribe(user, mediaType);
                if (mediaType === 'audio') {
                    const remoteAudioTrack = user.audioTrack;
                    remoteAudioTrack?.play();
                }

                dispatch(setAnswered(true));

                client.on('user-unpublished', async (user) => await client.unsubscribe(user));
            });
            dispatch(setRtcClient({...rtc, client}));
        };

        if(!rtc.client) {
            startBasicCall();
        }
    }, [dispatch, rtc]);

    return {createCall, joinCall, leaveCall}
};
